import { get, post, put } from '@classic/Foundation/Services/ApiService'
import type { AvatarList, FollowMeProfile, FollowMeSharedBet } from '@core/Areas/FollowMe/types'
import { CopiedFeedPageSize, ShareFeedPageSize } from '../constants'

const enum LocalConstants {
  FollowMeV1ApiBase = '/api/followme/v1',
}

export const postShareToFollowMe = ({ ticketSequence }: { ticketSequence: number }) =>
  post<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/share/bet`,
    body: { ticketSequence },
  })

export const postCreateFollowMeProfile = () =>
  post<CreateProfileResponse>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/`,
    body: {},
  })

export const getFollowMeSharedBetsFeed = async ({
  pageParam = null,
  sortOrder = 'sortAsc',
  sortFilter = 'JumpTime',
  isFollowing = false,
  accountId = null,
  fromPostId = undefined,
}: FollowMeFeedParams) => {
  const params = new URLSearchParams()
  params.append('pageSize', ShareFeedPageSize.toString())
  params.append('following', `${isFollowing}`)

  if (sortOrder === 'sortAsc') {
    params.append('sortAsc', sortFilter)
  } else {
    params.append('sortDesc', sortFilter)
  }

  if (fromPostId) {
    params.append('from', fromPostId.toString())
    params.append('newPosts', 'true')
  } else if (pageParam !== null) {
    params.append('from', pageParam.toString())
  }
  if (accountId !== null) {
    params.append('account', accountId)
  }

  const url = `${LocalConstants.FollowMeV1ApiBase}/share/feed?${params.toString()}`
  return get<FollowMeSharedBet[]>({ url })
}

export const getCopiedBetsFeed = async ({ pageParam = null }: { pageParam?: number | null }) => {
  const params = new URLSearchParams()
  params.append('pageSize', CopiedFeedPageSize.toString())
  if (pageParam !== null) {
    params.append('from', pageParam.toString())
  }

  const url = `${LocalConstants.FollowMeV1ApiBase}/share/copied?${params.toString()}`
  return get<FollowMeSharedBet[]>({ url })
}

export const getPrincipleFollowMeUserProfile = () => {
  return get<FollowMeProfile>({ url: `${LocalConstants.FollowMeV1ApiBase}/account` })
}

export const getFollowMeFollowingAccounts = () =>
  get<FollowMeProfile[]>({ url: `${LocalConstants.FollowMeV1ApiBase}/account/following` })

export function postFollowMeFollowAccount(accountId: string) {
  return post<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/follow`,
    body: { followsAccountId: accountId },
  })
}

export function postUnFollowMeFollowAccount(accountId: string) {
  return post<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/unfollow`,
    body: { accountToUnfollowId: accountId },
  })
}

export function postFollowedAccountNotifications(accountId: string, shouldNotify: boolean) {
  return post<void>({
    url: `${LocalConstants.FollowMeV1ApiBase}/account/notification/set`,
    body: { followedAccountId: accountId, notify: shouldNotify },
  })
}

export const getFollowMeUserProfileById = (id: string) =>
  get<FollowMeProfile>({ url: `${LocalConstants.FollowMeV1ApiBase}/account/${id}` })

export const getFollowMeAvatarList = async () => {
  return await get<AvatarList[]>({ url: `${LocalConstants.FollowMeV1ApiBase}/account/avatars` })
}

export const putFollowMeProfileDetails = (body: UpdateProfile) => {
  return put<UpdateProfile>({ url: `${LocalConstants.FollowMeV1ApiBase}/account`, body })
}

export const getFollowMeSharedBetByPostId = (postId: string) =>
  get<FollowMeSharedBet>({ url: `${LocalConstants.FollowMeV1ApiBase}/share/post/${postId}` })

// =====
// Types
// =====

type CreateProfileResponse = {
  accountId: string
  displayName: string
  description: string
  avatar: string
  color: string
  createdAt: string
}

export interface ProblemResponse {
  detail: string
  instance: string
  exception?: string
  requestId: string
  status: number
  title: string
  traceId: string
  type: string
}

export type UpdateProfile = {
  displayName?: string | null
  description?: string | null
  avatar?: string | null
  color?: string | null
}

export type FollowMeFeedParams = {
  pageParam?: number | null
  sortOrder?: string
  sortFilter?: string
  isFollowing?: boolean
  accountId?: string | null
  fromPostId?: number
}
